import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Shield,
  Server,
  ListChecks,
  CloudCog,
  Tags,
  RefreshCcw,
  FlaskConical,
  Mail,
  FolderSync,
  Play,
  LogOut,
  RadioTower,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

interface AdminLayoutProps {
  children: React.ReactNode;
  headerAction?: React.ReactNode;
}

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { name: 'Overview', href: '/admin', icon: Shield },
  { name: 'Shared Servers', href: '/admin/shared-servers', icon: Server },
  { name: 'Tasks', href: '/admin/tasks', icon: ListChecks },
  { name: 'B2 Credentials', href: '/admin/b2', icon: CloudCog },
  { name: 'Test Tags', href: '/admin/test-tags', icon: Tags },
  { name: 'Odoo Versions', href: '/admin/odoo-versions', icon: RefreshCcw },
  { name: 'Continuous Testing', href: '/admin/continuous-testing', icon: FlaskConical },
  { name: 'Email Tools', href: '/admin/email-tools', icon: Mail },
  { name: 'Project Tools', href: '/admin/project-tools', icon: FolderSync },
  { name: 'Playbook Runner', href: '/admin/playbook-runner', icon: Play },
];

export function AdminLayout({ children, headerAction }: AdminLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span className="font-semibold">Admin</span>
              </Link>
              <nav className="hidden lg:flex items-center gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-100',
                      location.pathname.startsWith(item.href) && 'bg-gray-100 text-primary'
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>

            <div className="flex items-center gap-3">
              {headerAction && <div className="hidden md:block">{headerAction}</div>}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <RadioTower className="h-4 w-4" />
                    <div className="text-left">
                      <p className="text-sm font-medium leading-tight">{user?.full_name || user?.email}</p>
                      <p className="text-xs text-muted-foreground leading-tight">Admin</p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Admin Menu</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/projects">Back to user dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="bg-white border-b border-gray-200 px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-primary hover:bg-gray-50',
                  location.pathname.startsWith(item.href) && 'bg-gray-100 text-primary'
                )}
              >
                <item.icon className="h-4 w-4" />
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6">{children}</main>
    </div>
  );
}
